import React, { useState, useEffect, useCallback, useMemo, useId } from "react";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { Table, Spinner, Modal, Button, Form } from "react-bootstrap";
import RefershIcon from "../../../common/assets/images/reload.png";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPenToSquare,
  faCircleCheck,
  faCircleXmark,
} from "@fortawesome/free-solid-svg-icons";
import Select from "react-select";
import axiosInstance from "../../../axiosinterceptor";
import "./RoleAssignments.css";
import debounce from "lodash.debounce";
import { useNavigate } from 'react-router-dom';
import { Tooltip } from "@mui/material";


const RoleAssignments = () => {
   const navigate = useNavigate();
   const permissions = JSON.parse(sessionStorage.getItem('permissions'));
    useEffect(() => {
              if (permissions && !permissions.admin) {
                  navigate('/no-access');
              }
          }, [permissions, navigate]);

  const [userList, setUserList] = useState([]);
  const [rolesList, setRolesList] = useState([]);
  const [userRoles, setUserRoles] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [selectedRoles, setSelectedRoles] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [refresh, setRefresh] = useState(false);
  const [roleAssignmentSearchUser, setRoleAssignmentSearchUser] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");


  const fetchRolesAndUsers = async () => {
    setIsLoading(true);
    try {
      // Uncomment the following lines to use API calls instead of static data
      const rolesResponse = await axiosInstance.get("/getAdminRolesACS");
      const roles = rolesResponse.data.data;
      const filteredRoles = roles.filter(
          (role) => role.role_name.toLowerCase() !== "admin\n"
      );
      setRolesList(filteredRoles);

      const usersResponse = await axiosInstance.get("/getAdminUsersACS");
      const users = usersResponse.data.data.map(user => ({
          user_id: user.user_id,
          username: user.username,
      }));
      setUserList(users);
      const userRolesResponse = await axiosInstance.get("/getUserRolesACS");
      setUserRoles(userRolesResponse.data.data);
    } catch (error) {
      console.log("Error fetching roles or users: ", error);
      // toast.error("Failed to fetch data. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchRolesAndUsers();
  }, [refresh]);

  const handleShowModal = (user = null) => {
    if (user) {
      handleEdit(user);
    } else {
      setSelectedUserId(null);
      setSelectedRoles([]);
      setIsEditing(false);
      setShowModal(true);
    }
  };

  const onSearchInputChange = (e) => {
    const value = e.target.value;
    setRoleAssignmentSearchUser(value);
    handleSearchChange(value);
  };

  const handleSearchChange = useCallback(
    debounce((value) => {
      setDebouncedSearch(value);
    }, 300),
    []
  );

  const filteredUsers = useMemo(
    () =>
      userList.filter((user) =>
        user.username.toLowerCase()?.includes(debouncedSearch?.toLowerCase())
      ),
    [debouncedSearch, userList]
  );

  const handleUserChange = (event) => {
    setSelectedUserId(event.target.value);
  };

  const handleRoleChange = (selectedOptions) => {
    const updatedRoles = selectedOptions.map((option) => option.value);
    setSelectedRoles(updatedRoles);
  };

  const assignRoles = async () => {
    if (!selectedUserId || selectedRoles.length === 0) {
      setErrorMessage("Please select a user and at least one role.");
      return;
    }
    const createdBy = sessionStorage.userId;
    try {
      // Uncomment the following lines to use API calls instead of static data
      const roleAssignments = selectedRoles.map(role => {
          const roleObj = rolesList.find(r => r.role_name === role);
          const roleId = roleObj ? roleObj.role_id : null;
          if (roleId) {
              const payload = {
                  user_id: parseInt(selectedUserId),
                  role_id: roleId,
                  created_by: createdBy
              };
              return axiosInstance.post("/postUserRolesACS", payload);
          }
          return Promise.resolve();
      });
      await Promise.all(roleAssignments);
      setShowModal(false);
      toast.success("Roles assigned successfully.");
      setRefresh(!refresh);
    } catch (error) {
      console.log("Error assigning roles:", error);
      toast.error("Failed to assign roles. Please try again.");
      setErrorMessage("Failed to assign roles. Please try again.");
    }
  };

  const handleEdit = (user) => {
    setSelectedUserId(user.user_id);
    const userRolesForEdit = userRoles
      .filter((roleAssignment) => roleAssignment.user_id === user.user_id)
      .map((roleAssignment) => {
        const role = rolesList.find(
          (r) => r.role_id === roleAssignment.role_id
        );
        return role ? role.role_name : null;
      })
      .filter((roleName) => roleName !== null);

    setSelectedRoles(userRolesForEdit);
    setIsEditing(true);
    setShowModal(true);
  };

  const updateRoles = async () => {
    if (!selectedUserId) {
      setErrorMessage("Please select a user and at least one role.");
      return;
    }
    const updatedRoleIds = selectedRoles
      .map((roleName) => {
        const roleObj = rolesList.find((role) => role.role_name === roleName);
        return roleObj ? roleObj.role_id : null;
      })
      .filter((roleId) => roleId !== null);
    // Uncomment the following lines to use API calls instead of static data
    const payload = {
        user_id: selectedUserId,
        role_id: updatedRoleIds
    };
    try {
      await axiosInstance.post("/update_admin_role_assign_acs", payload);
      setShowModal(false);
      toast.success("Roles updated successfully.");
      setRefresh(!refresh);
    } catch (error) {
      console.log("Error updating roles:", error);
      toast.error("Failed to update roles. Please try again.");
      setErrorMessage("Failed to update roles. Please try again.");
    }
  };

  const closeModal = () => {
    setShowModal(false);
    setErrorMessage("");
  };

  const userHasRole = (userId, roleId) => {
    return userRoles.some(
      (assignment) =>
        assignment.user_id === userId && assignment.role_id === roleId
    );
  };

  const handleRefresh = () => {
    setRefresh(!refresh);
  };

  // if (isLoading) {
  //   return <div className="container">Loading...</div>;
  // }

  return (
    <div className="userlist-section" style={{fontFamily: "sans-serif"}}>
      <ToastContainer autoClose={20000} />
      <div className="userlist-header">
        <h4 className="userlist-header-text">User Role Assignments</h4>
        <div className="userlist-filter-section">
          <input
            type="text"
            placeholder="Search..."
            className="role-assignments-search-input"
            value={roleAssignmentSearchUser}
            onChange={onSearchInputChange}
            style={{ marginRight: "10px" }}
          />
          <button
            className="role-assignments-filter-btn"
            onClick={() => handleShowModal()}
          >
            Assign Role
          </button>
          <span className="userlist-refresh-btn" onClick={handleRefresh}>
            <span>
              <img src={RefershIcon} alt="Refresh" />
            </span>
          </span>
        </div>
      </div>
      <div
        className="userlist-table-container"
        style={{ overflowX: "auto", scrollbarWidth: "thin" }}
      >
        {
          isLoading ? 
          <div className="role-assignment-loader">
            <p className="mb-0"><Spinner /></p>
          </div> : 
         <Table
         responsive="sm"
         style={{
           textAlign: "center",
           overflowY: "auto",
           scrollbarWidth: "thin",
         }}
         className="role-assignments-table table-striped"
       >
         <thead className="">
           <tr className="userlist-tr">
             {/* <th className="userlist-th">User ID</th> */}
             <th className="userlist-th fixed-column fixed-left">Username</th>
             {rolesList.map((role) => (
               <th key={role.role_id} className="userlist-th">
                 {/* {role.role_name} */}
                 <Tooltip title = {role.role_name}>
                 {role.role_name.slice(0, 10)}...
                 </Tooltip>
               </th>
             ))}
             <th className="userlist-th fixed-column fixed-left">Actions</th>
           </tr>
         </thead>
         <tbody>
           {filteredUsers && filteredUsers?.length > 0 ? (
             filteredUsers?.map((user) => (
               <tr key={user.user_id}>
                 <td className="userlist-td fixed-column  fixed-left">
                   {user.username}
                 </td>
                 {rolesList.map((role) => (
                   <td key={role.role_id} className="userlist-td">
                     {userHasRole(user.user_id, role.role_id) ? (
                       <FontAwesomeIcon
                         icon={faCircleCheck}
                         className="icon-checked"
                       />
                     ) : (
                       <FontAwesomeIcon
                         icon={faCircleXmark}
                         className="icon-unchecked"
                       />
                     )}
                   </td>
                 ))}
                 <td className="userlist-td fixed-column">
                   <FontAwesomeIcon
                     icon={faPenToSquare}
                     className="iconuser-update"
                     onClick={() => handleShowModal(user)}
                   />
                 </td>
               </tr>
             ))
           ) : (
             <tr>
               <td colSpan="14">
                 <p className="mb-0">No Records Found</p>
               </td>
             </tr>
           )}
         </tbody>
       </Table>
        }
      </div>

      <Modal show={showModal} onHide={closeModal} centered>
        <Modal.Header closeButton>
          <Modal.Title>
            {isEditing ? "Edit User Roles" : "Assign Roles to User"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group>
              <Form.Label>Select User</Form.Label>
              <Form.Control
                as="select"
                value={selectedUserId || ""}
                onChange={handleUserChange}
              >
                <option value="">Select User</option>
                {userList.map((user) => (
                  <option value={user.user_id} key={user.user_id}>
                    {user.username}
                  </option>
                ))}
              </Form.Control>
            </Form.Group>

            <Form.Group>
              <Form.Label>Select Roles</Form.Label>
              <Select
                className="userroleassignment"
                options={rolesList.map((role) => ({
                  value: role.role_name,
                  label: role.role_name,
                }))}
                isMulti
                value={
                  selectedRoles &&
                  selectedRoles.map((role) => ({ value: role, label: role }))
                }
                onChange={handleRoleChange}
                placeholder="Select roles..."
              />
            </Form.Group>

            <div className="role-container">
              {selectedRoles &&
                selectedRoles.map((role) => (
                  <div key={role} className="selected-role-tag">
                    {role}
                  </div>
                ))}
            </div>

            {errorMessage && <p className="error-message">{errorMessage}</p>}
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={closeModal}>
            Cancel
          </Button>
          <Button
            variant="success"
            onClick={isEditing ? updateRoles : assignRoles}
          >
            {isEditing ? "Update" : "Save Permission"}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default RoleAssignments;
